/**
 * Word Rungs — parity with Flutter `word_rungs_logic.dart`.
 */

import { getDateKeyForHash, hashStringJs32 } from '../LetterHive/letterHiveUtils';

export const WORD_RUNGS_WORD_LENGTH = 5;
export const WORD_RUNGS_MIN_EDGES = 3;
export const WORD_RUNGS_MAX_EDGES = 6;

export function wordRungsDailySeed(localDate = new Date()) {
  const key = getDateKeyForHash(localDate);
  return Math.abs(hashStringJs32(`word-rungs-${key}`));
}

export function wordRungsLeaderboardScore(transitions, elapsedSeconds) {
  const t = Math.min(Math.max(0, transitions | 0), 99);
  const s = Math.min(Math.max(0, elapsedSeconds | 0), 86400);
  return 1_000_000 - t * 10_000 - s;
}

export function oneLetterDifference(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) diff++;
    if (diff > 1) return false;
  }
  return diff === 1;
}

export function neighborsInDictionary(word, dict) {
  const w = word.toUpperCase();
  const out = [];
  for (let i = 0; i < w.length; i++) {
    for (let c = 65; c <= 90; c++) {
      const ch = String.fromCharCode(c);
      if (ch === w[i]) continue;
      const next = `${w.slice(0, i)}${ch}${w.slice(i + 1)}`;
      if (dict.has(next)) out.push(next);
    }
  }
  return out;
}

export function bfsFrom(start, dict) {
  const dist = new Map();
  const parent = new Map();
  const q = [];
  const s = start.toUpperCase();
  dist.set(s, 0);
  parent.set(s, null);
  q.push(s);
  while (q.length) {
    const cur = q.shift();
    const d = dist.get(cur);
    for (const n of neighborsInDictionary(cur, dict)) {
      if (!dist.has(n)) {
        dist.set(n, d + 1);
        parent.set(n, cur);
        q.push(n);
      }
    }
  }
  return { dist, parent };
}

export function shortestPath(start, end, dict) {
  const s = start.toUpperCase();
  const e = end.toUpperCase();
  if (!dict.has(s) || !dict.has(e)) return null;
  const { dist, parent } = bfsFrom(s, dict);
  if (!dist.has(e)) return null;
  const chain = [e];
  let cur = e;
  while (cur !== s) {
    const p = parent.get(cur);
    if (p == null) return null;
    chain.push(p);
    cur = p;
  }
  return chain.reverse();
}

export function isValidChain(chain, dict) {
  if (chain.length < 2) return false;
  for (const w of chain) {
    if (w.length !== WORD_RUNGS_WORD_LENGTH) return false;
    if (!dict.has(w.toUpperCase())) return false;
  }
  for (let i = 0; i < chain.length - 1; i++) {
    if (!oneLetterDifference(chain[i].toUpperCase(), chain[i + 1].toUpperCase())) {
      return false;
    }
  }
  return true;
}

/**
 * @param {Set<string>} dict — uppercase five-letter words only
 * @param {number} seed
 * @returns {{ start: string, end: string, optimalPath: string[], puzzleKey: string, optimalTransitions: number } | null}
 */
export function pickDailyPuzzle(dict, seed) {
  const words = [...dict].filter((w) => w.length === WORD_RUNGS_WORD_LENGTH).sort();
  if (words.length < 2) return null;

  const n = words.length;
  for (let attempt = 0; attempt < 120; attempt++) {
    const s0 = seed + attempt * 97;
    const start = words[s0 % n];
    const { dist } = bfsFrom(start, dict);

    for (let edges = WORD_RUNGS_MIN_EDGES; edges <= WORD_RUNGS_MAX_EDGES; edges++) {
      const ends = words.filter((w) => w !== start && dist.get(w) === edges);
      if (!ends.length) continue;
      const end = ends[Math.floor(s0 / 11) % ends.length];
      const path = shortestPath(start, end, dict);
      if (path && path.length >= 2) {
        const optimalPath = path;
        const puzzleKey = `${start.toUpperCase()}|${end.toUpperCase()}`;
        return {
          start,
          end,
          optimalPath,
          puzzleKey,
          optimalTransitions: optimalPath.length - 1,
        };
      }
    }
  }
  return null;
}

export function resolveDailyPuzzle(dict, seed) {
  return (
    pickDailyPuzzle(dict, seed) ||
    pickDailyPuzzle(dict, seed + 1337) ||
    pickDailyPuzzle(dict, seed + 99991)
  );
}

export function pickPracticePuzzle(dict) {
  const seed = Math.floor(Math.random() * (1 << 30));
  return pickDailyPuzzle(dict, seed);
}
